/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Mantine
import {
	ActionIcon,
	Badge,
	Box,
	Flex,
	Group,
	Image,
	Paper,
} from '@mantine/core';
import {
	useDisclosure,
	useElementSize,
	useWindowScroll,
} from '@mantine/hooks';

// Styles
import classes from './measurability-projects.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import {
	IconFilter,
	IconPlus,
} from '@tabler/icons-react';

// Icons
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { useMeasurabilityProjects } from '@features/catalogs/meausrability-projects/list/api/get-measurability-projects';
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/list/hooks/useMeasurabilityProjects';
import { useAuthorization } from '@lib/authorization';
import { isNil } from 'lodash';
import { useNavigate } from 'react-router';
import { useCountryCodes } from 'shared/api/get-country-codes';

import { Button } from '@components/v2/ui/Button';
import { Text } from '@components/v2/ui/Text';
import {
	TViewMode,
	ViewToggle,
} from '@components/v2/ui/ViewToggle/ViewToggle';

import { Datatable } from '@components/v2/ui/Datatable/Datatable';
import { Pagination } from '@components/v2/ui/Pagination';
import { ProjectCard } from '@components/v2/ui/ProjectCard';
import { ProjectCardSkeleton } from '@components/v2/ui/ProjectCard/ProjectCardSkeleton';
import { NOT_APPLICABLE } from '@config/constants/common';
import { AnimatePresence, motion } from 'framer-motion';

// Datatable config
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { tableSchemaBase as columns } from '@features/catalogs/meausrability-projects/list/catalog-measurability.columns';
import { Filter } from '@features/catalogs/meausrability-projects/list/components';
import { useActivateMeasurabilityProject } from '@features/resources/measurabilities/api/activate-measurability-project';
import { TMeasurabilityProject } from '@global-types/api/api';
import { checkAclFront } from '@utils/acl-front';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeasurabilityProjects = () => {
	{
		/* *************************** HOOKS *************************** */
	}
	const { t } = useTranslation();
	const [isOverflowFilters] = useState(false);
	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const { data: countryCodes } = useCountryCodes(
		'measurabilities'
	);

	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const {
		handleChangePaginationSize,
		handleChangePagination,
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams,
		measurabilityProjectsSortParams,
		handleTechnologyFilter,
		handleCountryFilter,
		handleProjectNameFilter,
		addMeasurabilityProjectFormOpened,
		setAddMeasurabilityProjectFormOpened,
		handleOpenDrawerAddMeasurabilityProject,
		handleActiveFilter,
	} = useMeasurabilityProject();
	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	// APIs
	const {
		data: measurabilityProjects,
		total: totalItems,
		totalPages,
		isPending,
	} = useMeasurabilityProjects(
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams
	);

	const { mutate } = useActivateMeasurabilityProject(
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams,
		''
	);

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const { checkRole, role, checkAcls } =
		useAuthorization();

	const [mode, setMode] = useState<TViewMode>('grid');

	const [, scrollTo] = useWindowScroll();

	useEffect(() => {
		scrollTo({ y: 0 });
	}, []);

	const [filtersNbr, setFiltersNbr] = useState(0);

	return (
		<Paper className={classes['main-layout']}>
			<Flex flex={1} direction={'column'}>
				{checkAclFront(
					ACLS.FRONT.MEASURABILITIES.FILTER,
					role as ROLES
				) && (
					<Filter
						opened={openedFilters}
						onClose={closeFilters}
						title={t('filters.titles.projects')}
						onApplyFilters={(filters) => {
							setFiltersNbr(Object.keys(filters).length);
						}}
						value={measurabilityProjectsQueryParams}
					/>
				)}
				<Box className={classes['action-bar']}>
					<Flex
						flex={1}
						justify={'space-between'}
						wrap={'wrap'}
					>
						<ViewToggle mode={mode} onChange={setMode} />
						<Group gap={'var(--mantine-spacing-xs)'}>
							{checkAclFront(
								ACLS.FRONT.MEASURABILITIES.FILTER,
								role as ROLES
							) && (
								<div
									style={{
										position: 'relative',
									}}
								>
									{filtersNbr > 0 && (
										<Badge
											style={{
												position: 'absolute',
												zIndex: '3',
												right: '-3px',
												fontFamily:
													'InterDisplay-Regular',
											}}
											color="var(--mantine-color-igluRed-9)"
											size="xs"
											circle
										>
											{filtersNbr}
										</Badge>
									)}
									<ActionIcon
										variant="outline"
										size="48px"
										radius="xl"
										color={
											'var(--mantine-color-igluBorder-9)'
										}
										data-testid="bell-icon-button"
										onClick={openFilters}
										bg={
											filtersNbr
												? 'var(--mantine-color-igluPrimary-9)'
												: 'transparent'
										}
									>
										<IconFilter
											color={
												filtersNbr
													? 'var(--mantine-color-igluTextButton-9)'
													: 'var(--mantine-color-igluPrimary-9)'
											}
											radius="xl"
											size={24}
											stroke={1.5}
										/>
									</ActionIcon>
								</div>
							)}
							{checkAcls([
								ACLS.MEASURABILITIES.UPDATE,
							]) && (
								<Button
									disabled
									leftSection={
										<IconPlus
											radius="xl"
											size={24}
											stroke={1.5}
										/>
									}
									variant={'primary'}
								>
									{t(
										'measurabilities.addMeasurabilities'
									)}
								</Button>
							)}
						</Group>
					</Flex>
				</Box>

				{mode == 'list' && (
					<Flex
						wrap={'wrap'}
						gap={'var(--mantine-spacing-sm)'}
					>
						<AnimatePresence
							key={'List mode'}
							mode="wait"
						>
							<motion.div
								key={'empty'}
								initial={{ y: 10, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: -10, opacity: 0 }}
								transition={{ duration: 0.2 }}
								style={{
									display: 'flex',
									flex: 1,
								}}
							>
								{Array.isArray(measurabilityProjects) && (
									<Datatable
										allColumns={columns}
										records={measurabilityProjects}
										columns={
											!checkRole([ROLES.BRAND_ADMIN])
												? columns
												: columns.filter(
														(col) =>
															col.accessor !== 'status'
													)
										}
										onStatusProjectChange={async (
											row: any
										) => {
											if (
												!checkRole([
													ROLES.PRODUCER_STAFF,
													ROLES.PRODUCER_ADMIN,
													ROLES.ORGANIZATION_ADMIN,
												])
											)
												await mutate({
													active: !row.active,
													ids: [row.id],
												});
										}}
										onRowClick={(row: {
											record: TMeasurabilityProject;
										}) => {
											navigate(
												`/measurability-projects/${row.record.id}`
											);
										}}
									/>
								)}
							</motion.div>
						</AnimatePresence>
					</Flex>
				)}
				{mode == 'grid' && (
					<Box
						style={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fill, minmax(300px, 1fr))',
							gap: 'var(--mantine-spacing-sm)',
						}}
					>
						{isPending
							? [...Array(10).keys()].map(
									(project, index) => (
										<AnimatePresence
											key={index}
											mode="wait"
										>
											<motion.div
												key={'empty'}
												initial={{ y: 10, opacity: 0 }}
												animate={{ y: 0, opacity: 1 }}
												exit={{ y: -10, opacity: 0 }}
												transition={{ duration: 0.2 }}
												style={{
													display: 'flex',
													flex: 1,
												}}
											>
												<ProjectCardSkeleton
													fullWidth={true}
												/>
											</motion.div>
										</AnimatePresence>
									)
								)
							: measurabilityProjects &&
								Array.isArray(measurabilityProjects) &&
								measurabilityProjects.length > 0 &&
								measurabilityProjects.map((project) => (
									<AnimatePresence
										key={project.id}
										mode="wait"
									>
										<motion.div
											key={project.id}
											initial={{ y: 10, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -10, opacity: 0 }}
											transition={{ duration: 0.2 }}
											style={{
												display: 'flex',
												flex: 1,
											}}
										>
											<ProjectCard
												cover={project?.image}
												type="measurability"
												projectType={project?.type}
												name={project?.name}
												active={project?.active}
												fullWidth={true}
												supportedByLogo={
													project?.supportedBy
												}
												location={{
													countryCode:
														project?.country?.code ||
														NOT_APPLICABLE,
													place:
														project?.location?.place ||
														NOT_APPLICABLE,
													countryName:
														project?.country?.name ||
														NOT_APPLICABLE,
												}}
												onStatusChange={async () => {
													await mutate({
														active: !project.active,
														ids: [project.id],
													});
												}}
												availableStock={
													project?.availableStock
												}
												sellPrice={project?.sellPrice}
												onClick={() => {
													navigate(
														`/measurability-projects/${project.id}`
													);
												}}
												showStatus={checkRole([
													ROLES.ADMIN,
													ROLES.SUPERADMIN,
													ROLES.ORGANIZATION_ADMIN,
													ROLES.PRODUCER_ADMIN,
													ROLES.PRODUCER_STAFF,
												])}
												readonly={checkRole([
													ROLES.PRODUCER_ADMIN,
													ROLES.PRODUCER_STAFF,
													ROLES.ORGANIZATION_ADMIN,
												])}
											/>
										</motion.div>
									</AnimatePresence>
								))}
					</Box>
				)}
				{mode == 'grid' &&
					measurabilityProjects &&
					Array.isArray(measurabilityProjects) &&
					measurabilityProjects.length == 0 &&
					!isPending && (
						<Box className={classes.containerEmptyList}>
							<Box
								className={
									classes.containerTextsNoRecords
								}
							>
								<Image
									src={NoRecordsIcon}
									className={classes.iconNoRecords}
								/>
								<>
									<Text
										className={classes.titleNoRecords}
										variant="subtitle1"
										c="var(--mantine-color-igluPrimary-9)"
									>
										{t('global.datatable.noResultsFound')}
									</Text>
									<Text
										className={
											classes.descriptionNoRecords
										}
										variant="body2"
									>
										{t(
											'global.datatable.noResultsFoundDescription'
										)}
									</Text>
								</>
							</Box>
						</Box>
					)}
			</Flex>
			<Box className={classes['pagination-bar']}>
				<Pagination
					pageSize={measurabilityProjectsPageSize}
					totalItems={totalItems}
					activePage={measurabilityProjectsPage}
					onPageSizeChange={handleChangePaginationSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Paper>
	);
};

export default MeasurabilityProjects;
